import { NotFoundPage, RequireOrganization } from '@startupquickstart/frontend';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { DashboardPage } from './pages/DashboardPage';
import { PersonalAccountHome } from './pages/PersonalAccountHome';
import { TeamPage } from './pages/team/TeamPage';

// Standalone pages (no layout)
import { AcceptInvitationPage } from './pages/accept-invitation/AcceptInvitationPage';
import { EventDisplayRoute } from './pages/events/EventDisplayPage';
import { QrPosterPage } from './pages/qr/QrPosterPage';

// Lazy-loaded so the markdown renderer (react-markdown + plugins) is only
// fetched when a user opens the in-app guide, keeping it out of the main bundle.
const HelpGuidePage = lazy(() =>
  import('./pages/help/HelpGuidePage').then((m) => ({ default: m.HelpGuidePage })),
);

// Challenge pages
import { ChallengeDemoTab } from './pages/challenges/ChallengeDemoTab';
import { ChallengeDetailPage } from './pages/challenges/ChallengeDetailPage';
import { ChallengeFormPage } from './pages/challenges/ChallengeFormPage';
import { ChallengeOverviewTab } from './pages/challenges/ChallengeOverviewTab';
import { ChallengesPage } from './pages/challenges/ChallengesPage';

// Artifact pages
import { ArtifactDetailPage } from './pages/artifacts/ArtifactDetailPage';
import { ArtifactFormPage } from './pages/artifacts/ArtifactFormPage';
import { ArtifactDemoTab } from './pages/artifacts/ArtifactDemoTab';
import { ArtifactOverviewTab } from './pages/artifacts/ArtifactOverviewTab';
import { ArtifactsPage } from './pages/artifacts/ArtifactsPage';

// Collectible pages (DT-155)
import { CollectibleDetailPage } from './pages/collectibles/CollectibleDetailPage';
import { CollectibleFormPage } from './pages/collectibles/CollectibleFormPage';
import { CollectibleOverviewTab } from './pages/collectibles/CollectibleOverviewTab';
import { CollectiblesPage } from './pages/collectibles/CollectiblesPage';

// Greater Areas pages
import { GreaterAreasPage } from './pages/greater-areas/GreaterAreasPage';
import { GreaterAreaFormPage } from './pages/greater-areas/GreaterAreaFormPage';

// Template pages
import { TemplateArtifactsTab } from './pages/templates/TemplateArtifactsTab';
import { TemplateBrandingTab } from './pages/templates/TemplateBrandingTab';
import { TemplateChallengesTab } from './pages/templates/TemplateChallengesTab';
import { TemplateCollectiblesTab } from './pages/templates/TemplateCollectiblesTab';
import { TemplateDetailPage } from './pages/templates/TemplateDetailPage';
import { TemplateStacksTab } from './pages/templates/TemplateStacksTab';
import { TemplateTeamsTab } from './pages/templates/TemplateTeamsTab';
import { TemplateFormPage } from './pages/templates/TemplateFormPage';
import { TemplateOverviewTab } from './pages/templates/TemplateOverviewTab';
import { TemplatesPage } from './pages/templates/TemplatesPage';
import { TemplateChallengeSettingsPage } from './pages/templates/TemplateChallengeSettingsPage';

// Event pages
import { EventArtifactsTab } from './pages/events/EventArtifactsTab';
import { EventCollectiblesTab } from './pages/events/EventCollectiblesTab';
import { EventStacksTab } from './pages/events/EventStacksTab';
import { EventChallengeDemoTab } from './pages/events/challenges/EventChallengeDemoTab';
import { EventChallengeDetailPage } from './pages/events/EventChallengeDetailPage';
import { EventChallengeFormPage } from './pages/events/EventChallengeFormPage';
import { EventChallengesTab } from './pages/events/EventChallengesTab';
import { EventChatTab } from './pages/events/EventChatTab';
import { EventDetailPage } from './pages/events/EventDetailPage';
import { EventFormPage } from './pages/events/EventFormPage';
import { EventOverviewTab } from './pages/events/EventOverviewTab';
import { EventsPage } from './pages/events/EventsPage';
import { EventActivityTab } from './pages/events/EventActivityTab';
import { EventAlliancesTab } from './pages/events/EventAlliancesTab';
import { EventBrandingTab } from './pages/events/EventBrandingTab';
import { EventLiveTab } from './pages/events/EventLiveTab';
import { EventSubmissionsTab } from './pages/events/EventSubmissionsTab';
import { EventTeamDetailPage } from './pages/events/EventTeamDetailPage';
import { EventTeamsTab } from './pages/events/EventTeamsTab';
import { EventTeamOverviewTab } from './pages/events/teams/EventTeamOverviewTab';
import { EventTeamMembersTab } from './pages/events/teams/EventTeamMembersTab';
import { EventTeamChallengesTab } from './pages/events/teams/EventTeamChallengesTab';
import { EventTeamActivityTab } from './pages/events/teams/EventTeamActivityTab';
import { EventTeamChatTab } from './pages/events/teams/EventTeamChatTab';
import { EventChallengeOverviewTab } from './pages/events/challenges/EventChallengeOverviewTab';
import { EventChallengeSubmissionsTab } from './pages/events/challenges/EventChallengeSubmissionsTab';

export const router = createBrowserRouter([
  // Standalone display route (no layout wrapper) - for TV/projector display.
  // EventDisplayRoute gates on RequireOrganization unless a `?token=`
  // display token is present (token-scoped, read-only, validated server-side).
  {
    path: '/events/:id/display',
    element: <EventDisplayRoute />,
  },
  // Standalone full-screen QR poster (no layout/auth) — print or show to scan.
  { path: '/qr', element: <QrPosterPage /> },
  // Standalone invitation-accept landing (pre-auth) — Clerk redirects here
  // with __clerk_ticket after the invitee clicks their email link.
  { path: '/accept-invitation', element: <AcceptInvitationPage /> },
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      // Personal account home page (mobile app)
      {
        path: 'home',
        element: <PersonalAccountHome />,
      },
      // Dashboard
      {
        path: 'dashboard',
        element: (
          <RequireOrganization>
            <DashboardPage />
          </RequireOrganization>
        ),
      },

      // Challenge Library
      {
        path: 'challenges',
        element: (
          <RequireOrganization>
            <ChallengesPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'challenges/new',
        element: (
          <RequireOrganization>
            <ChallengeFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'challenges/:id',
        element: (
          <RequireOrganization>
            <ChallengeDetailPage />
          </RequireOrganization>
        ),
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: <ChallengeOverviewTab /> },
          { path: 'demo', element: <ChallengeDemoTab /> },
        ],
      },
      {
        path: 'challenges/:id/edit',
        element: (
          <RequireOrganization>
            <ChallengeFormPage />
          </RequireOrganization>
        ),
      },

      // Artifact Library
      {
        path: 'artifacts',
        element: (
          <RequireOrganization>
            <ArtifactsPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'artifacts/new',
        element: (
          <RequireOrganization>
            <ArtifactFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'artifacts/:id',
        element: (
          <RequireOrganization>
            <ArtifactDetailPage />
          </RequireOrganization>
        ),
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: <ArtifactOverviewTab /> },
          { path: 'demo', element: <ArtifactDemoTab /> },
        ],
      },
      {
        path: 'artifacts/:id/edit',
        element: (
          <RequireOrganization>
            <ArtifactFormPage />
          </RequireOrganization>
        ),
      },

      // Collectible Library (DT-155)
      // Greater Areas (managed cities/regions for library challenges)
      {
        path: 'greater-areas',
        element: (
          <RequireOrganization>
            <GreaterAreasPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'greater-areas/new',
        element: (
          <RequireOrganization>
            <GreaterAreaFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'greater-areas/:id/edit',
        element: (
          <RequireOrganization>
            <GreaterAreaFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'collectibles',
        element: (
          <RequireOrganization>
            <CollectiblesPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'collectibles/new',
        element: (
          <RequireOrganization>
            <CollectibleFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'collectibles/:id',
        element: (
          <RequireOrganization>
            <CollectibleDetailPage />
          </RequireOrganization>
        ),
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: <CollectibleOverviewTab /> },
        ],
      },
      {
        path: 'collectibles/:id/edit',
        element: (
          <RequireOrganization>
            <CollectibleFormPage />
          </RequireOrganization>
        ),
      },

      // Game Templates
      {
        path: 'templates',
        element: (
          <RequireOrganization>
            <TemplatesPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'templates/new',
        element: (
          <RequireOrganization>
            <TemplateFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'templates/:id',
        element: (
          <RequireOrganization>
            <TemplateDetailPage />
          </RequireOrganization>
        ),
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: <TemplateOverviewTab /> },
          { path: 'challenges', element: <TemplateChallengesTab /> },
          { path: 'stacks', element: <TemplateStacksTab /> },
          { path: 'artifacts', element: <TemplateArtifactsTab /> },
          { path: 'collectibles', element: <TemplateCollectiblesTab /> },
          { path: 'teams', element: <TemplateTeamsTab /> },
          { path: 'branding', element: <TemplateBrandingTab /> },
        ],
      },
      {
        path: 'templates/:id/edit',
        element: (
          <RequireOrganization>
            <TemplateFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'templates/:id/challenges/:linkId/settings',
        element: (
          <RequireOrganization>
            <TemplateChallengeSettingsPage />
          </RequireOrganization>
        ),
      },

      // Events
      {
        path: 'events',
        element: (
          <RequireOrganization>
            <EventsPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'events/new',
        element: (
          <RequireOrganization>
            <EventFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'events/:id',
        element: (
          <RequireOrganization>
            <EventDetailPage />
          </RequireOrganization>
        ),
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: <EventOverviewTab /> },
          { path: 'challenges', element: <EventChallengesTab /> },
          { path: 'artifacts', element: <EventArtifactsTab /> },
          { path: 'collectibles', element: <EventCollectiblesTab /> },
          { path: 'stacks', element: <EventStacksTab /> },
          { path: 'teams', element: <EventTeamsTab /> },
          { path: 'alliances', element: <EventAlliancesTab /> },
          { path: 'chat', element: <EventChatTab /> },
          { path: 'activity', element: <EventActivityTab /> },
          { path: 'branding', element: <EventBrandingTab /> },
          { path: 'live', element: <EventLiveTab /> },
          { path: 'submissions', element: <EventSubmissionsTab /> },
          // Old Facilitator tab folded into the Live command center.
          { path: 'facilitator', element: <Navigate to="../live" replace /> },
        ],
      },
      {
        path: 'events/:id/edit',
        element: (
          <RequireOrganization>
            <EventFormPage />
          </RequireOrganization>
        ),
      },
      // Create a brand-new challenge directly in the event (optionally
      // pre-grouped via ?groupId=). Static "new" segment outranks the
      // dynamic :challengeId detail route below.
      {
        path: 'events/:id/challenges/new',
        element: (
          <RequireOrganization>
            <EventChallengeFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'events/:id/challenges/:challengeId/edit',
        element: (
          <RequireOrganization>
            <EventChallengeFormPage />
          </RequireOrganization>
        ),
      },
      // Team detail — owns its own header (with QR hero) and tab
      // strip. Top-level instead of nested under EventDetailPage so
      // we don't render tabs-inside-tabs.
      {
        path: 'events/:id/teams/:teamId',
        element: (
          <RequireOrganization>
            <EventTeamDetailPage />
          </RequireOrganization>
        ),
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: <EventTeamOverviewTab /> },
          { path: 'members', element: <EventTeamMembersTab /> },
          { path: 'challenges', element: <EventTeamChallengesTab /> },
          { path: 'activity', element: <EventTeamActivityTab /> },
          { path: 'chat', element: <EventTeamChatTab /> },
        ],
      },
      // Challenge detail — same pattern as team. QR (when set) is
      // the hero image; tabs cover overview + submissions.
      {
        path: 'events/:id/challenges/:challengeId',
        element: (
          <RequireOrganization>
            <EventChallengeDetailPage />
          </RequireOrganization>
        ),
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: <EventChallengeOverviewTab /> },
          { path: 'demo', element: <EventChallengeDemoTab /> },
          { path: 'submissions', element: <EventChallengeSubmissionsTab /> },
        ],
      },

      // Organization team management (members + invitations)
      {
        path: 'team',
        element: (
          <RequireOrganization>
            <TeamPage />
          </RequireOrganization>
        ),
      },

      // Help & user guide
      {
        path: 'help',
        element: (
          <RequireOrganization>
            <Suspense fallback={<div className="p-6 text-muted-foreground">Loading guide…</div>}>
              <HelpGuidePage />
            </Suspense>
          </RequireOrganization>
        ),
      },
      // fallback
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
